import React from 'react';
import {
  Hero,
  HighlightedCard,
  LeadParagraph,
  LinkButton,
  ListedLink,
  StaticNotification,
  TextBlock,
} from '@sato/components';
import classNames from 'classnames';
import { observer } from 'mobx-react-lite';

import { Link } from '@components/common/Link';
import { Header } from '@components/Header';
import { localizationStore } from '@stores/localization';
import { getLinkIcon } from '@utils/link.utils';
import { localizeDate } from '@utils/localize.utils';

import { EntryType, isContentType, ParsedEntry } from '../types';
import { textAlignMap } from '../utils/entry.utils';

import { ConditionalWrapper } from './ConditionalWrapper';
import { ContentfulLinkButton } from './ContentfulLinkButton';
import { GenericWidget } from './GenericWidget';
import { IconCardLink } from './IconCardLink';
import { InlineDynamicText } from './InlineDynamicText';
import { ListItem } from './ListItem';
import { ResolveMediaEntry } from './ResolveMediaEntry';
import { SubPageNav } from './SubPageNav';
import { Tabs } from './Tabs';
import {
  Accordion,
  Dialog,
  Grid,
  LinkList,
  PromoCard,
  PromotionSection,
  Section,
} from '.';

/** Utility component for resolving and rendering Contentful data objects as elements */
export const ResolveEntry = observer(
  <T extends EntryType = EntryType>({
    entry: data,
    className,
    options,
  }: {
    entry: ParsedEntry<T>;
    className?: string;
    options?: Partial<{
      isInline?: boolean;
      inheritLayout?: boolean;
    }>;
  }) => {
    const language = localizationStore.language;

    try {
      if (isContentType(data, 'pageSection')) {
        const { layout, content, ...section } = data;

        return (
          <Section
            {...section}
            content={content}
            layout={options?.inheritLayout ? 'inherit' : layout?.layout}
            className={options?.inheritLayout ? 'py-0' : undefined}
          />
        );
      } else if (isContentType(data, 'richText')) {
        const { richText, text, id, textPosition, limitTextWidth } = data;
        const content = richText ?? text;

        if (!content) return null;

        return (
          <TextBlock
            id={id}
            linkComponent={Link}
            imgSource="contentful"
            bodyVariant="normal"
            className={classNames([
              textPosition && textAlignMap[textPosition],
              limitTextWidth && 'max-w-3xl',
            ])}
          >
            {content}
          </TextBlock>
        );
      } else if (isContentType(data, 'collapsableContent')) {
        const { content, ...collapsableContent } = data;

        if (!content?.length) return null;

        return <Accordion {...collapsableContent}>{content}</Accordion>;
      } else if (isContentType(data, 'header')) {
        const { image, heading, subHeading, leadText, buttons, breadcrumbs } =
          data;

        if (image) {
          return (
            <Hero
              headingStyle="h1"
              heading={heading}
              image={image}
              body={
                <div>
                  <LeadParagraph>{leadText}</LeadParagraph>

                  <div className="mt-5 flex flex-col gap-4">
                    {buttons?.map((button) => (
                      <LinkButton
                        key={button.id}
                        linkComponent={Link}
                        to={button.url}
                        variant="filled-white"
                        buttonSize="large"
                        textClassName="!text-5"
                        className="!px-6 !py-[1.25rem] mdDown:w-full"
                      >
                        {button.text}
                      </LinkButton>
                    ))}
                  </div>
                </div>
              }
            />
          );
        }

        return (
          <Header
            title={heading}
            subHeading={subHeading}
            leadText={leadText}
            className="lg:mb-4"
            {...(breadcrumbs && {
              breadcrumbs: [
                ...breadcrumbs.map(({ title, url }) => ({
                  children: title,
                  to: url,
                })),
                { children: heading },
              ],
            })}
          />
        );
      } else if (isContentType(data, 'grid')) {
        const grid = data;

        if (!grid.content?.length) return null;

        return <Grid {...grid} />;
      } else if (isContentType(data, 'image')) {
        return (
          <ResolveMediaEntry
            entry={data}
            options={{
              fill: false,
              className: 'py-5',
              isExpandable: data.allowFullScreen,
            }}
          />
        );
      } else if (isContentType(data, 'video')) {
        return <ResolveMediaEntry entry={data} />;
      } else if (isContentType(data, 'promotionSection')) {
        return <PromotionSection {...data} />;
      } else if (isContentType(data, 'promoCard')) {
        const variant = data.variant;

        if (variant === 'highlighted') {
          const {
            icon,
            heading,
            body,
            richText,
            linkText,
            url,
            colorVariant,
            id,
          } = data;

          return (
            <HighlightedCard
              id={id}
              color={
                colorVariant === 'neutral' || colorVariant === 'highlighted'
                  ? 'tertiary'
                  : (colorVariant ?? 'primary')
              }
              icon={icon?.icon}
              title={heading}
              body={richText ?? body}
              // stretch to available space
              className="w-full self-stretch"
              {...(url && {
                buttonProps: {
                  wrapperComponent: Link,
                  to: url,
                  children: linkText,
                  icon: 'arrowRight',
                  iconPosition: 'right',
                },
              })}
            />
          );
        }

        if (variant === 'staticNotificationCard') {
          return (
            <StaticNotification
              icon={data.icon?.icon}
              title={data.heading}
              body={data.richText ?? data.body}
              buttonComponent={Link}
              variant="card"
              iconPosition="top"
              {...(data.url && {
                buttonProps: {
                  wrapperComponent: Link,
                  to: data.url,
                  children: data.linkText,
                  icon: 'arrowRight',
                  iconPosition: 'right',
                },
              })}
              className="z-10 w-full flex-1 self-stretch md:p-4 lg:px-5 mdDown:p-3 lgDown:flex-row"
              iconClassName="lgDown:w-6 lgDown:h-6"
            />
          );
        }

        return <PromoCard {...data} variant={variant} />;
      } else if (isContentType(data, 'listItem')) {
        return <ListItem {...data} />;
      } else if (isContentType(data, 'linkButton')) {
        return <ContentfulLinkButton {...data} className={className} />;
      } else if (isContentType(data, 'listedLink')) {
        const { url, icon, date, ...listedLink } = data;

        // resolve listedLink only if it's translated in the current language
        if (!url || !listedLink.title) return null;

        return (
          <ListedLink
            {...listedLink}
            linkComponent={Link}
            linkProps={{ to: url }}
            preTitle={date ? localizeDate(new Date(date), language) : undefined}
            icon={icon?.icon}
            arrowIcon={getLinkIcon(url, 'arrowRight')}
          />
        );
      } else if (isContentType(data, 'listedLinkList')) {
        return <LinkList data={data} />;
      } else if (isContentType(data, 'dialog')) {
        return <Dialog data={data} />;
      } else if (isContentType(data, 'subPageNav')) {
        return <SubPageNav data={data} />;
      } else if (isContentType(data, 'iconCardLink')) {
        return <IconCardLink data={data} />;
      } else if (isContentType(data, 'staticNotification')) {
        const {
          title,
          richTextBody,
          body,
          variant,
          icon,
          iconPosition,
          linkButton,
        } = data;

        return (
          <StaticNotification
            title={title}
            body={richTextBody ?? body}
            variant={variant}
            icon={icon?.icon}
            iconPosition={iconPosition}
            {...(linkButton && {
              buttonComponent: ResolveEntry,
              buttonProps: {
                entry: linkButton,
                iconPosition: linkButton.iconPosition,
              },
            })}
            className="z-10 w-full"
          />
        );
      } else if (isContentType(data, 'widget')) {
        return <GenericWidget data={data} />;
      } else if (isContentType(data, 'inlineDynamicText')) {
        return <InlineDynamicText {...data} />;
      } else if (isContentType(data, 'conditionalWrapper')) {
        return <ConditionalWrapper {...data} />;
      } else if (isContentType(data, 'tabs')) {
        return <Tabs {...data} />;
      }
      throw new Error(`Unsupported content type: ${data.contentType}`);
    } catch (e) {
      console.error('> Failed to parse object', data);
      return null;
    }
  }
);
